import React, { useState } from 'react'
import App from './App'
import { NO_IMAGE_SOURCE } from './shared/GlobalConstants'
import { validateEmail, showFlashMessage } from '../helpers/utils'
import Loader from './shared/Loader'
import LogoutButton from './shared/LogoutButton'

export default function MyAccount({ appProps, tenantRef, screenClass, logoutPath }) {
  const displayName = appProps.account.firstName || ''
  const displayEmail = appProps.account.unconfirmedEmail || appProps.account.email

  const [firstName, setFirstName] = useState(appProps.account.firstName || '')
  const [lastName, setLastName] = useState(appProps.account.lastName || '')
  const [email, setEmail] = useState(appProps.account.unconfirmedEmail || appProps.account.email)
  const [oldPassword, setOldPassword] = useState('')
  const [password, setPassword] = useState('')
  const [editPassword, setEditPassword] = useState(false)
  const [selectedFile, setSelectedFile] = useState(undefined)
  const [avatar, setAvatar] = useState(appProps.account.avatarUrl)
  const [removeAvatar, setRemoveAvatar] = useState(false)
  const [loading, setLoading] = useState(false)
  const [passwordValidator, setPasswordValidator] = useState({})

  const disableSave = editPassword && !(passwordValidator.length && passwordValidator.number && passwordValidator.uppercase && passwordValidator.lowercase && oldPassword)
  const profileName = [firstName, lastName].filter(Boolean).join(' ') || displayName || displayEmail
  const visibleAvatar = (!removeAvatar && avatar) || NO_IMAGE_SOURCE

  function handlePassword(event) {
    const value = event.target.value
    let validations = {}

    if (value.length >= 8) {
      validations = { ...validations, length: true }
    }
    if (value.match(/^.*\d.*$/)) {
      validations = { ...validations, number: true }
    }
    if (value.match(/^.*[A-Z].*$/)) {
      validations = { ...validations, uppercase: true }
    }
    if (value.match(/^.*[a-z].*$/)) {
      validations = { ...validations, lowercase: true }
    }

    setPasswordValidator(validations)
    setPassword(value)
  }

  function abortPasswordEdit() {
    setEditPassword(false)
    setPassword('')
    setPasswordValidator({})
    setOldPassword('')
  }

  function saveAccount() {
    if(email.length === 0) {
      showFlashMessage(`${Cas.translations.email_blank}`, 'error')
    } else if(!validateEmail(email)) {
      showFlashMessage(`${Cas.translations.email_invalid}`, 'error')
    } else {
      setLoading(true)

      let formData = new FormData()
      formData.append('account[first_name]', firstName.trim())
      formData.append('account[last_name]', lastName.trim())
      formData.append('account[email]', email)
      formData.append('account[password]', password)
      formData.append('account[old_password]', oldPassword)

      if(selectedFile) {
        formData.append('account[avatar]', selectedFile)
      } else if(removeAvatar) {
        formData.append('remove_avatar', removeAvatar)
      }

      Rails.ajax({
        type: 'PATCH',
        url: '/update_profile',
        dataType: 'json',
        data: formData,
        success: res => {
          if(res.forceRedirect) {
            window.location.reload()
          } else if(res.account) {
            setAvatar(res.account.avatarUrl)
            setRemoveAvatar(false)
            setSelectedFile(undefined)
            setEditPassword(false)
            setPassword('')
            setPasswordValidator({})
            setOldPassword('')
            showFlashMessage(Cas.translations.action_saved, 'success')
          }
        },
        error: res => {
          if (res.errors) {
            showFlashMessage(res.errors, 'error')
          } else {
            showFlashMessage(Cas.translations.error_generic, 'error')
          }
        },
        complete: res => {
          setLoading(false)
        }
      })
    }
  }

  function renderPasswordValidator(key, valid) {
    return (
      <div className={`pass-validator ${valid ? '' : 'invalid'} `}>
        <i className={`fa-light ${valid ? 'fa-circle-check' : 'fa-circle'}`}></i>
        <span>{ key }</span>
      </div>
    )
  }

  return (
    <App appProps={ appProps }>
      <div className={ `${screenClass}` }>
        <section className='create-instance-hero profile-hero'>
          <div className='create-instance-hero-copy'>
            <div className='eyebrow'>{ Cas.translations.profile_eyebrow }</div>
            <h1>{`${Cas.translations.profile_title}, ${profileName}`}</h1>
            <p>{Cas.translations.profile_subtitle}</p>
          </div>

          <div className='profile-hero-card'>
            <div className='profile-avatar-shell'>
              <img src={ selectedFile ? URL.createObjectURL(selectedFile) : visibleAvatar } />
              <label className='profile-avatar-edit' htmlFor='avatar'>
                <i className='fa-light fa-camera'></i>
                <input
                  className='edit-avatar'
                  type='file'
                  id='avatar'
                  accept='image/png, image/jpeg'
                  onChange={ event => setSelectedFile(event.target.files[0]) }
                />
              </label>
            </div>
            <div className='profile-hero-meta'>
              <div>{ profileName }</div>
              <span>{ displayEmail }</span>
            </div>
            { !removeAvatar && avatar &&
              <button className='button unstyled profile-remove-avatar' onClick={() => {
                setRemoveAvatar(true)
                setSelectedFile(undefined)
              }}>
                {Cas.translations.delete_button}
              </button>
            }
          </div>
        </section>

        <section className='create-instance-flow' aria-label={ Cas.translations.profile_flow_label }>
          <div className='flow-step active'>
            <i className='fa-light fa-id-card'></i>
            <span>{ Cas.translations.profile_identity_step }</span>
          </div>
          <div className='flow-line'></div>
          <div className={`flow-step ${appProps.account.unconfirmedEmail ? '' : 'active'}`}>
            <i className='fa-light fa-envelope-circle-check'></i>
            <span>{ Cas.translations.profile_email_step }</span>
          </div>
          <div className='flow-line'></div>
          <div className={`flow-step ${editPassword ? 'active' : ''}`}>
            <i className='fa-light fa-shield-keyhole'></i>
            <span>{ Cas.translations.profile_security_step }</span>
          </div>
        </section>

        <section className='create-instance-wrapper profile-shell'>
          <div className='section-heading'>
            <div>
              <div className='eyebrow'>{ Cas.translations.profile_details_eyebrow }</div>
              <h2>{ Cas.translations.profile_details_title }</h2>
            </div>
            <div className='availability-pill profile-status-pill'>
              <i className={ `fa-light ${appProps.account.unconfirmedEmail ? 'fa-triangle-exclamation' : 'fa-circle-check'}` }></i>
              <span>{ appProps.account.unconfirmedEmail ? Cas.translations.profile_email_pending : Cas.translations.profile_email_confirmed }</span>
            </div>
          </div>

          <div className='profile-grid'>
            <div className='profile-panel identity-panel'>
              <div className='profile-panel-title'>
                <i className='fa-light fa-user-pen'></i>
                <div>
                  <h3>{ Cas.translations.profile_identity_title }</h3>
                  <p>{ Cas.translations.profile_identity_description }</p>
                </div>
              </div>

              <div className='profile-fields-grid'>
                <div className='field flex-box flex-column'>
                  <label>{Cas.translations.first_name}</label>
                  <input type='text' value={firstName} onChange={e => setFirstName(e.target.value)} />
                </div>

                <div className='field flex-box flex-column'>
                  <label>{Cas.translations.last_name}</label>
                  <input type='text' value={lastName} onChange={e => setLastName(e.target.value)} />
                </div>

                <div className='field flex-box flex-column full-width'>
                  <label>{`${Cas.translations.email} *`}</label>
                  <input type='text' value={email} onChange={e => setEmail(e.target.value)} />
                </div>
              </div>

              { appProps.account.unconfirmedEmail &&
                <div className='pending-confirmation-warning field flex-box'>
                  <i className='fa-light fa-triangle-exclamation'></i>
                  <div className='flex-box flex-column'>
                    <div>
                      {Cas.translations.email_confirmation_needed_start}&nbsp;
                      <b>{appProps.account.unconfirmedEmail}</b>&nbsp;
                      {Cas.translations.email_confirmation_needed_end}
                    </div>
                    <div>
                      {Cas.translations.email_pending_confirmation_login}&nbsp;
                      <b>{appProps.account.email}</b>.
                    </div>
                    <a href='/reconfirmation' rel='nofollow noindex noreferrer' data-method='post'>{Cas.translations.resend_confirmation_instructions}</a>
                  </div>
                </div>
              }
            </div>

            <div className='profile-panel security-panel'>
              <div className='profile-panel-title'>
                <i className='fa-light fa-lock-keyhole'></i>
                <div>
                  <h3>{ Cas.translations.profile_security_title }</h3>
                  <p>{ Cas.translations.profile_security_description }</p>
                </div>
              </div>

              { editPassword ?
                <div className='profile-password-edit'>
                  <div className='field flex-box flex-column'>
                    <label>{ `${ Cas.translations.old_password } *` }</label>
                    <input
                      type='password'
                      placeholder='•••••••••••'
                      value={ oldPassword }
                      onChange={ event => setOldPassword(event.target.value) }
                    />
                  </div>

                  <div className='field flex-box flex-column'>
                    <label>{ `${ Cas.translations.new_password } *` }</label>
                    <input
                      type='password'
                      placeholder='•••••••••••'
                      value={ password }
                      onChange={ handlePassword }
                    />
                  </div>

                  <div className='password-rules'>
                    { renderPasswordValidator(Cas.translations.password_length, passwordValidator.length) }
                    { renderPasswordValidator(Cas.translations.password_number, passwordValidator.number) }
                    { renderPasswordValidator(Cas.translations.password_uppercase, passwordValidator.uppercase) }
                    { renderPasswordValidator(Cas.translations.password_lowercase, passwordValidator.lowercase) }
                  </div>

                  <div className='profile-password-edit-actions'>
                    <button className='button unstyled profile-edit-button profile-cancel-password-button' onClick={abortPasswordEdit}>
                      <i className='fa-light fa-xmark'></i>
                      {Cas.translations.cancel}
                    </button>
                  </div>
                </div>
                :
                <div className='profile-password-summary'>
                  <div>
                    <span>{ Cas.translations.password }</span>
                    <strong>•••••••••••</strong>
                  </div>
                  <button className='button unstyled profile-edit-button' onClick={() => setEditPassword(true)}>
                    <i className='fa-light fa-pen'></i>
                    {Cas.translations.edit_button}
                  </button>
                </div>
              }
            </div>
          </div>

          <div className='profile-actions'>
            { loading ?
              <Loader size={'small'} position={'centered'} />
              :
              <button
                className={ `button ${ disableSave ? 'disabled' : '' }` }
                disabled={ disableSave }
                onClick={ saveAccount }
              >
                {Cas.translations.save_changes_button}
              </button>
            }

            <LogoutButton logoutPath={logoutPath} className='button unstyled logout-button' />
          </div>
        </section>
      </div>
    </App>
  )
}
